import { useState, useEffect, useRef, useCallback } from "react";
import type { MmUser } from "./api/mm/mm.types";
import type * as featureList from "./portals/messagemedia/featureList";

export type Features = (typeof featureList)[keyof typeof featureList];

export const userFeatureHasAccess = (
  routePermissions?: string[],
  userPermissions: MmUser["featureSet"]["features"] = []
) =>
  routePermissions
    ? routePermissions.some((permission) =>
        userPermissions.includes(permission)
      )
    : true;

export const userPermissionHasAccess = (
  routePermissions: readonly string[] | undefined,
  userPermissions: string[] = []
) =>
  routePermissions
    ? routePermissions.some((permission) =>
        userPermissions.includes(permission)
      )
    : true;

const getParsedValidatedValue = <Data>(
  key: string,
  value: string | null,
  validator: (x: unknown) => x is Data
): Data | undefined => {
  if (!value) {
    return undefined;
  }
  try {
    const parsedValue = JSON.parse(value);
    if (validator(parsedValue)) {
      return parsedValue;
    } else {
      console.warn("Failed to validate localStorage value for key", key);
      return undefined;
    }
  } catch {
    console.warn(`Failed to parse localStorage value for key ${key}`);
    return undefined;
  }
};

/**
 * Returns tuple with value inside storage or default and a function to update the value in the storage
 * The hook listens for storage events to know if the value should be updated
 */
export function useLocalStorage<Data>(
  key: string,
  validator: (x: unknown) => x is Data,
  defaultValue: Data
) {
  const [value, setValue] = useState<Data>(() => {
    const value = window.localStorage.getItem(key);
    return getParsedValidatedValue(key, value, validator) ?? defaultValue;
  });

  useEffect(() => {
    const storageEventListener = (event: StorageEvent) => {
      if (event.key === key) {
        const parsedValidValue = getParsedValidatedValue(
          key,
          event.newValue,
          validator
        );

        if (parsedValidValue) {
          setValue(parsedValidValue);
        }
      }
    };

    window.addEventListener("storage", storageEventListener);
    return () => window.removeEventListener("storage", storageEventListener);
    // Should be called only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStorageValue = (newValue: Data) => {
    localStorage.setItem(key, JSON.stringify(newValue));

    // Needs to dispatch an event for the change to be propagated on the same tab.
    // See: https://stackoverflow.com/a/65348883
    window.dispatchEvent(
      new StorageEvent("storage", { key, newValue: JSON.stringify(newValue) })
    );
  };

  return [value, setStorageValue] as const;
}
export function useThrottle<Args extends unknown[]>(
  callback: (...args: Args) => void,
  delayMs = 200
) {
  const isThrottled = useRef(false);

  const throttledCallback = useCallback(
    (...args: Args) => {
      if (isThrottled.current) return;

      callback(...args);
      isThrottled.current = true;
      setTimeout(() => (isThrottled.current = false), delayMs);
    },
    [callback, delayMs]
  );

  return throttledCallback;
}
